// cubeViewer.js
// Simple CSS-3D cube that renders each face using the existing PIXI + TileManager pipeline.
// Each face uses {s} in URL_TEMPLATE as the cube face id (e.g. px,nx,py,ny,pz,nz).

import { TileManager } from "./tiles.js";

function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }

export function mountCubeViewer({ host, data }) {
  const { BASE_W, BASE_H, TILE_SIZE, URL_TEMPLATE, MAX_Z, RAW } = data;
  const cubeCfg = RAW?.page?.cube || RAW?.cube || {};
  // data.json often provides faces as an array of strings, e.g. ["px","nx",...].
  // Normalize to [{id,label}, ...] so the rest of this module can rely on f.id.
  const faces = Array.isArray(cubeCfg.faces)
    ? cubeCfg.faces.map((f) => {
        if (typeof f === "string") return { id: f, label: f };
        if (f && typeof f === "object") return { id: f.id, label: f.label || f.id };
        return null;
      }).filter(Boolean)
    : [
        { id: "pz", label: "front" },
        { id: "nz", label: "back" },
        { id: "px", label: "right" },
        { id: "nx", label: "left" },
        { id: "py", label: "top" },
        { id: "ny", label: "bottom" },
      ];

  
  const ORIENT_KEYS = new Set(["px","nx","py","ny","pz","nz"]);
  const inferOrient = (f, idx) => {
    const idStr = String(f.id);
    if (ORIENT_KEYS.has(idStr)) return idStr;
    const lab = String(f.label || "").toLowerCase();
    if (lab.includes("front")) return "pz";
    if (lab.includes("back")) return "nz";
    if (lab.includes("right")) return "px";
    if (lab.includes("left")) return "nx";
    if (lab.includes("top")) return "py";
    if (lab.includes("bottom")) return "ny";
    // common numeric ordering: 0=left,1=front,2=right,3=back,4=top,5=bottom
    const n = Number(idStr);
    if (Number.isFinite(n)) {
      const map = {0:"nx",1:"pz",2:"px",3:"nz",4:"py",5:"ny"};
      if (map[n] ) return map[n];
    }
    // fallback based on index order if nothing else
    const mapIdx = {0:"pz",1:"nz",2:"px",3:"nx",4:"py",5:"ny"};
    return mapIdx[idx] || "pz";
  };
// face size in CSS pixels (render targets will resize with host)
  // Support both "facePx" (viewer config) and "size" (data.json cube.size)
  const FACE_PX = Number(cubeCfg.facePx || cubeCfg.size || 512);

  // Shared texture cache across faces (reduces duplicate textures / requests)
  const sharedTexCache = { texCache: new Map(), clock: { value: 0 } };

  // ---- DOM ----
  host.innerHTML = "";
  host.classList.add("viewer-host");

  const stage = document.createElement("div");
  stage.className = "cube-stage";
  stage.tabIndex = 0;

  const cube = document.createElement("div");
  cube.className = "cube";
  stage.appendChild(cube);
  host.appendChild(stage);

  const faceEls = new Map();
  for (let i=0;i<faces.length;i++) {
    const f = faces[i];
    const tileId = String(f.id);
    const orient = inferOrient(f, i);
    const el = document.createElement("div");
    el.className = `cube-face cube-face-${orient}`;
    el.dataset.face = tileId;
    el.title = f.label || f.id;
    cube.appendChild(el);
    faceEls.set(tileId, el);
  }

  // ---- PIXI per face ----
  const faceApps = [];
  const destroyAll = () => {
    for (const fa of faceApps) {
      try {
        fa.app.destroy(true, { children: true, texture: false, baseTexture: false });
      } catch (e) {}
    }
    host.innerHTML = "";
  };

  const makeFaceApp = (faceId, faceEl) => {
    // PIXI app
    const app = new PIXI.Application({
      resizeTo: faceEl,
      backgroundAlpha: 0,
      antialias: true,
      autoDensity: true,
      resolution: Math.max(1, window.devicePixelRatio || 1),
    });
    faceEl.appendChild(app.view);

    const world = new PIXI.Container();
    world.sortableChildren = true;
    app.stage.addChild(world);

    // view: fit full image into a square face
    const view = { tx: 0, ty: 0, scale: 1 };
    const applyTransform = () => {
      world.scale.set(view.scale);
      world.position.set(view.tx, view.ty);
    };

    const tiles = new TileManager({
      app,
      world,
      BASE_W,
      BASE_H,
      TILE_SIZE,
      URL_TEMPLATE,
      MAX_Z,
      sourceId: faceId,
      sharedTexCache,
    });

    const fit = () => {
      // Fit base image into the face canvas in *CSS pixels* (independent of devicePixelRatio).
      const w = app.renderer.screen.width;
      const h = app.renderer.screen.height;
      const s = Math.min(w / BASE_W, h / BASE_H);
      tiles.FIT_SCALE = s;
      view.scale = clamp(s, 0.05, Math.pow(2, Math.max(0, MAX_Z)));
      view.tx = (w - BASE_W * view.scale) * 0.5;
      view.ty = (h - BASE_H * view.scale) * 0.5;
      applyTransform();
      // initial load (full face at base zoom); higher LOD is handled by cube frustum logic
      tiles.requestTiles(view);
    };

    // NOTE: we do NOT request tiles every frame here.
    // In skybox mode, frustum/visibility is driven by the cube CSS transform;
    // cubeViewer will call tiles.requestTiles(...) with a per-face visible rect.

    // initial fit next frame (renderer size is valid)
    app.ticker.addOnce(fit);

    return { app, tiles, view, applyTransform, fit, faceEl, faceId };
  };

  for (const f of faces) {
    const tileId = String(f.id);
    const el = faceEls.get(tileId);
    if (!el) continue;
    faceApps.push(makeFaceApp(tileId, el));
  }

  // ---- cube interaction (rotate + zoom) ----
  let rotX = Number(cubeCfg.startRotX ?? -15);
  let rotY = Number(cubeCfg.startRotY ?? 25);
  let cubeScale = Number(cubeCfg.startScale ?? 1.0);
  let dragging = false;
  let lastX = 0;
  let lastY = 0;

  // --- frustum-ish tile requests ---
  // We approximate the per-face visible region by intersecting the projected
  // DOM rect of each face with the stage rect, then mapping that intersection
  // back into the face canvas coordinate system.
  const rectIntersect = (a, b) => {
    const l = Math.max(a.left, b.left);
    const t = Math.max(a.top, b.top);
    const r = Math.min(a.right, b.right);
    const bb = Math.min(a.bottom, b.bottom);
    if (r <= l || bb <= t) return null;
    return { left: l, top: t, right: r, bottom: bb, width: r - l, height: bb - t };
  };

  const clampRectToImage = (vis) => {
    return {
      l: clamp(vis.l, 0, BASE_W),
      t: clamp(vis.t, 0, BASE_H),
      r: clamp(vis.r, 0, BASE_W),
      b: clamp(vis.b, 0, BASE_H),
    };
  };

  const faceVisibleBaseRect = (fa, reqView, stageRect) => {
    const faceRect = fa.faceEl.getBoundingClientRect();
    const inter = rectIntersect(faceRect, stageRect);
    if (!inter) return null;

    // tiny intersection => treat as not visible (avoid thrashing on edge cases)
    const minArea = 8 * 8;
    if (inter.width * inter.height < minArea) return null;

    // Map screen->local face canvas coords (approx; ignores perspective skew)
    const w = fa.app.renderer.screen.width;
    const h = fa.app.renderer.screen.height;
    const sx = w / Math.max(1, faceRect.width);
    const sy = h / Math.max(1, faceRect.height);

    const localL = (inter.left - faceRect.left) * sx;
    const localR = (inter.right - faceRect.left) * sx;
    const localT = (inter.top - faceRect.top) * sy;
    const localB = (inter.bottom - faceRect.top) * sy;

    // Convert local canvas coords -> base-image coords using reqView transform
    const inv = 1 / Math.max(1e-9, reqView.scale);
    const vis = {
      l: (localL - reqView.tx) * inv,
      t: (localT - reqView.ty) * inv,
      r: (localR - reqView.tx) * inv,
      b: (localB - reqView.ty) * inv,
    };

    // If the intersection maps fully outside, skip
    if (vis.r <= 0 || vis.b <= 0 || vis.l >= BASE_W || vis.t >= BASE_H) return null;
    return clampRectToImage(vis);
  };

  let lodRaf = 0;
  const scheduleLOD = () => {
    if (lodRaf) return;
    lodRaf = requestAnimationFrame(() => {
      lodRaf = 0;
      updateLOD();
    });
  };

  const updateLOD = () => {
    const stageRect = stage.getBoundingClientRect();

    for (const fa of faceApps) {
      const baseView = fa.view;
      const w = fa.app.renderer.screen.width;
      const h = fa.app.renderer.screen.height;

      // Larger cubeScale => zoom in (narrower FOV) => request higher z.
      // Keep rendering transforms unchanged; this is only for tile selection.
      const virtualScale = baseView.scale / cubeScale;
      const reqView = {
        scale: virtualScale,
        tx: (w - BASE_W * virtualScale) * 0.5,
        ty: (h - BASE_H * virtualScale) * 0.5,
      };

      const vis = faceVisibleBaseRect(fa, reqView, stageRect);

      if (vis) {
        // Visible: load only the on-screen portion at the right LOD (+fallback layers)
        fa.tiles.requestTiles(reqView, vis);
      } else {
        // Not visible: keep only base layer (z≈0) around to avoid black flashes
        const full = { l: 0, t: 0, r: BASE_W, b: BASE_H };
        fa.tiles.requestTiles(baseView, full);
      }
    }
  };

  const updateCubeTransform = () => {
    cube.style.setProperty("--cube-face", `${FACE_PX}px`);
    const basePersp = Math.max(800, FACE_PX * 3);
    // Use cubeScale as a FOV control (zoom) instead of physically scaling the cube.
    // This keeps faces perfectly aligned (no gaps) at all zoom levels.
    const persp = basePersp / cubeScale;
    stage.style.setProperty("--cube-persp", `${persp}px`);
    // Move cube so CSS camera (at z=persp) is at cube center -> true skybox
    cube.style.transform = `translateZ(${persp}px) rotateX(${rotX}deg) rotateY(${-rotY}deg)`;
    // Move cube so the CSS "camera" (at z=persp) sits at cube center -> true skybox
    // Keep cube centered while scaling: translate AFTER scale
  };
  updateCubeTransform();
  // after initial layout is ready, compute frustum and request correct tiles
  scheduleLOD();

  const onDown = (e) => {
    dragging = true;
    lastX = e.clientX;
    lastY = e.clientY;
    stage.setPointerCapture?.(e.pointerId);
  };
  const onMove = (e) => {
    if (!dragging) return;
    const dx = e.clientX - lastX;
    const dy = e.clientY - lastY;
    lastX = e.clientX;
    lastY = e.clientY;
    rotY += dx * 0.35;
    rotX -= dy * 0.35;
    rotX = clamp(rotX, -89, 89);
    updateCubeTransform();
    scheduleLOD();
  };
  const onUp = () => { dragging = false; };
  const onWheel = (e) => {
    e.preventDefault();
    const delta = Math.sign(e.deltaY);
    const factor = (delta > 0) ? 0.92 : 1.08; // wheel down -> zoom out, wheel up -> zoom in

    // Skybox zoom = change FOV (CSS perspective), NOT scaling the face images.
    cubeScale *= factor;
    cubeScale = clamp(cubeScale, 0.25, 32); // allow reaching MAX_Z layers
    updateCubeTransform();
    scheduleLOD();
  };

  stage.addEventListener("pointerdown", onDown);
  stage.addEventListener("pointermove", onMove);
  stage.addEventListener("pointerup", onUp);
  stage.addEventListener("pointercancel", onUp);
  stage.addEventListener("wheel", onWheel, { passive: false });

  // Keep face canvases square-ish and readable: CSS decides size; PIXI uses resizeTo.
  // We still refit on window resize.
  const onResize = () => {
    for (const fa of faceApps) fa.fit?.();
    updateCubeTransform();
    scheduleLOD();
  };
  window.addEventListener("resize", onResize);

  return {
    type: "cube",
    destroy: () => {
      window.removeEventListener("resize", onResize);
      stage.removeEventListener("pointerdown", onDown);
      stage.removeEventListener("pointermove", onMove);
      stage.removeEventListener("pointerup", onUp);
      stage.removeEventListener("pointercancel", onUp);
      stage.removeEventListener("wheel", onWheel);
      destroyAll();
    },
  };
}